import React, { useEffect } from "react";
import { DBApplication } from "../interfaces/CoObject";
import { ObjectPanelTabs } from "./co-panel/ObjectPanelComponent";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { TgkParameterMetadata } from "../../api/models/tgk-parameter-metadata";
import { ParameterInput } from "../generic/ParameterInput";
import { DimensionDetail } from "../../api/models/dimension-detail";
import { AvailableParameters } from "./co-panel/ObjectPanelComponentHook";
import { Scenario } from "../../api/models/scenario";

export interface CoObjectParametersTableProps {
  objectParameters: DimensionDetail[];
  updateCoObjectParametersTable: (obj: DimensionDetail[], tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  dbApplication: DBApplication;
  formId: string;
  availableParametersMetadata: AvailableParameters;
  inheritableParameters: DimensionDetail[] | undefined;
  referringScenarios: Scenario;
}

export const CoObjectParametersTablePanel: React.FunctionComponent<CoObjectParametersTableProps> = (props) => {
  const {
    objectParameters: _objectParameters,
    updateCoObjectParametersTable,
    dbApplication,
    formId,
    availableParametersMetadata,
    inheritableParameters,
    isComponentInstance,
    isLocked,
  } = props;
  //copying required to not propagate unwanted changes
  const objectParameters =
    _objectParameters?.map((d) => {
      return {
        ...d,
        values: d.values?.map((v) => v),
      };
    }) ?? [];

  function validateFields(parameters: DimensionDetail[], mandatoryParameters: TgkParameterMetadata[]): ObjectPanelTabs {
    const getValidation = (isValid: boolean) => ({ key: props.tab.key, name: props.tab.name, isValid: isValid });

    if (parameters == null) return getValidation(false);
    for (const mandatoryParameterRoot of mandatoryParameters) {
      if (mandatoryParameterRoot.type === "dimension") {
        const thisParameterValue = parameters.find(
          (d) => d.structureCode === mandatoryParameterRoot.dimensionCode && d.code === mandatoryParameterRoot.code
        );
        if (!thisParameterValue?.values?.length) return getValidation(false);
      } else {
        const thisParameterValue = parameters.find((d) => d.dimensionOid === mandatoryParameterRoot.oid);
        if (!thisParameterValue?.values[0]?.desc?.toString()?.length) return getValidation(false);
      }
    }
    // no mandatory parameters
    return getValidation(true);
  }

  useEffect(() => {
    if (!props.isEdit)
      updateCoObjectParametersTable(
        objectParameters,
        validateFields(objectParameters, availableParametersMetadata.mandatoryParametersMetadata),
        false
      );
  }, []);

  if (
    availableParametersMetadata.mandatoryParametersMetadata.length ||
    availableParametersMetadata.optionalParametersMetadata.length
  )
    return (
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          {[
            ...availableParametersMetadata.mandatoryParametersMetadata,
            ...availableParametersMetadata.optionalParametersMetadata,
          ].map((metadata) => (
            <ParameterInput
              key={metadata.oid}
              metadata={metadata}
              dbApplication={dbApplication}
              formId={formId}
              objectParameters={objectParameters}
              inheritableParameters={inheritableParameters}
              isComponentInstance={isComponentInstance}
              isLocked={isLocked}
              updateParameters={(newParameters) => {
                updateCoObjectParametersTable(
                  newParameters,
                  validateFields(newParameters, availableParametersMetadata.mandatoryParametersMetadata),
                  true
                );
              }}
              referringScenarios={props.referringScenarios}
            />
          ))}
        </div>
      </div>
    );
  else
    return (
      <div
        style={{
          backgroundColor: "var(--color-yellow-a20)",
          fontSize: "1rem",
          margin: "1.25rem 1.25rem 1.25rem 1.25rem",
          padding: "0.5rem 1.25rem 0.5rem 1.25rem",
        }}
      >
        {formatMessage("cof.createnewobject.noparameters.info")}
      </div>
    );
};
